import PropTypes from 'prop-types'
import React, { useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'

import LoadingSpinner from '../../Shared/LoadingSpinner'
import ErrorMessage from '../../Shared/ErrorMessage'

import { initStateArray, arrayReducer } from '../../../reducers/default'
import StackedBar from '../../Shared/StackedBar'

/**
 * The event capacity as one 100% wide bar, split into segments by whatever
 * `fetchData` breaks the sold tickets down by (price range, ticket type). The
 * part that hasn't been sold yet stays empty, so the bar fills up over time.
 * Without a capacity there is nothing to fill up and nothing is rendered.
 * @Tags( event, api )
 * @Endpoints( /events/[eid]/tickets, /events/[eid]/lookout_prediction_benchmark )
 * @ApiLogic( ../../api/charts )
 */
export default function TicketSplitBar({ eventId, capacity, fetchData, onHasData }) {
  const { t } = useTranslation()
  const [series, dispatch] = useReducer(arrayReducer, initStateArray)
  const [translation] = useState({
    fullPrice: t('Event.ticketType.fullPrice'),
    regularDiscount: t('Event.ticketType.regularDiscount'),
    discount: t('Event.ticketType.discount'),
    subscription: t('Event.ticketType.subscription'),
    reserved: t('Event.ticketType.reserved'),
    free: t('Event.ticketType.free'),
    unsold: t('Event.ticketType.unsold'),
  })

  useEffect(() => {
    if (!(capacity > 0)) return
    fetchData(dispatch, eventId, translation, capacity)
  }, [fetchData, eventId, translation, capacity])

  useEffect(() => {
    onHasData?.(capacity > 0 && !series.empty)
  }, [capacity, series.empty, onHasData])

  // Without a capacity there is nothing to fill up.
  if (!(capacity > 0)) return null
  if (!series.loading && !series.error && series.empty) return null

  return (
    <div>
      {series.loading && <LoadingSpinner />}
      {series.error && <ErrorMessage danger>{t(series.message)}</ErrorMessage>}
      {!series.loading && series.items.length > 0 ? (
        <StackedBar data={series.items} summaryLabel={t('Event.ticketType.soldShare')} />
      ) : null}
    </div>
  )
}

TicketSplitBar.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  capacity: PropTypes.number,
  // Loads the segments of the bar, see `api/charts`.
  fetchData: PropTypes.func.isRequired,
  onHasData: PropTypes.func,
}
